import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { AppLayout } from "@/components/AppLayout";
import {
  BarChart3, Users, MessageSquare, Brain, Heart, HardDrive, Shield,
  TrendingUp, Clock, Loader2, RefreshCw, Target, Layers, Globe,
  AlertTriangle, AlertCircle, Info, Flame, Bookmark, Share2,
  CalendarDays, UserPlus, Activity, Zap, Timer,
  DollarSign, CreditCard, Gauge, ArrowUpRight, ArrowDownRight, Wallet, Server,
  RotateCcw, Search, Tag, Plus, ChevronDown, ShieldAlert, CheckCircle2, XCircle, Eye, Wifi,
  PenLine, Star, Mail, Download, Image, FileText, Package, ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ProvidersPanel } from "@/components/ProvidersPanel";

function MetricCard({ icon: Icon, label, value, sub, color = "text-primary", bgColor = "bg-primary/10", testId }) {
  return (
    <div data-testid={testId || `metric-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-card border border-border/50 rounded-2xl p-3.5 sm:p-4">
      <div className="flex items-start justify-between mb-2">
        <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center", bgColor)}>
          <Icon className={cn("w-4 h-4", color)} strokeWidth={1.5} />
        </div>
      </div>
      <p className="text-xl sm:text-2xl font-heading font-bold tracking-tight">{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
      {sub && <p className="text-[9px] text-muted-foreground/50 mt-0.5">{sub}</p>}
    </div>
  );
}

function BarRow({ label, value, max, color = "bg-primary" }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-[11px] text-muted-foreground w-20 sm:w-24 truncate text-right">{label}</span>
      <div className="flex-1 h-[6px] rounded-full bg-muted/50 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", color)} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
      <span className="text-[11px] font-semibold w-8 text-right">{value}</span>
    </div>
  );
}

const ALERT_STYLES = {
  critical: { icon: AlertTriangle, bg: "bg-red-50 dark:bg-red-950/20", border: "border-red-200 dark:border-red-900/40", text: "text-red-700 dark:text-red-400" },
  warning: { icon: AlertCircle, bg: "bg-amber-50 dark:bg-amber-950/20", border: "border-amber-200 dark:border-amber-900/40", text: "text-amber-700 dark:text-amber-400" },
  info: { icon: Info, bg: "bg-blue-50 dark:bg-blue-950/20", border: "border-blue-200 dark:border-blue-900/40", text: "text-blue-700 dark:text-blue-400" },
};

const FEED_ICONS = { signup: UserPlus, mood: Heart, backup: HardDrive };

function StockGauge({ label, usedPct, cost, budget, icon: GIcon, color }) {
  const remaining = Math.max(0, 100 - usedPct);
  const barColor = remaining > 50 ? "bg-emerald-500" : remaining > 20 ? "bg-amber-500" : "bg-red-500";
  return (
    <div data-testid={`stock-${label.toLowerCase()}`} className="bg-card border border-border/50 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <GIcon className={cn("w-4 h-4", color)} strokeWidth={1.5} />
          <span className="text-xs font-semibold">{label}</span>
        </div>
        <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded-full",
          remaining > 50 ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
          : remaining > 20 ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
          : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
        )}>
          {remaining.toFixed(0)}% left
        </span>
      </div>
      <div className="w-full h-2 rounded-full bg-muted/50 overflow-hidden mb-2">
        <div className={cn("h-full rounded-full transition-all duration-500", barColor)} style={{ width: `${Math.min(usedPct, 100)}%` }} />
      </div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground/60">
        <span>${cost.toFixed(2)} used</span>
        <span>${budget.toFixed(2)} budget</span>
      </div>
    </div>
  );
}

function FinancialCenter({ data }) {
  const f = data;
  const c = f.costs;
  const r = f.revenue;
  const isProfitable = r.net_monthly >= 0;

  return (
    <div className="space-y-5" data-testid="financials-section">
      {/* Revenue vs Cost Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        <MetricCard icon={DollarSign} label="Monthly Revenue" value={`$${r.monthly.toFixed(2)}`} sub={`$${r.annual_projected.toFixed(0)}/yr projected`} color="text-emerald-600" bgColor="bg-emerald-100 dark:bg-emerald-900/30" testId="metric-revenue" />
        <MetricCard icon={Wallet} label="Monthly Cost" value={`$${c.projected_monthly.toFixed(2)}`} sub={`$${c.daily_burn.toFixed(2)}/day burn`} color="text-red-600" bgColor="bg-red-100 dark:bg-red-900/30" testId="metric-cost" />
        <MetricCard icon={isProfitable ? ArrowUpRight : ArrowDownRight} label="Net Margin" value={`$${r.net_monthly.toFixed(2)}`} sub={`${r.margin_pct}% margin`} color={isProfitable ? "text-emerald-600" : "text-red-600"} bgColor={isProfitable ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-red-100 dark:bg-red-900/30"} testId="metric-margin" />
        <MetricCard icon={Users} label="Cost per User" value={`$${c.cost_per_user.toFixed(2)}`} sub="per month" color="text-blue-600" bgColor="bg-blue-100 dark:bg-blue-900/30" testId="metric-cpu" />
        <MetricCard icon={Zap} label="Today's Cost" value={`$${c.total_today.toFixed(2)}`} sub={`${f.today_ai_messages} AI msgs`} color="text-amber-600" bgColor="bg-amber-100 dark:bg-amber-900/30" testId="metric-today-cost" />
      </div>

      {/* API Stock Levels */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
          <Gauge className="w-4 h-4 text-primary" /> API Credit Levels & Stock
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <StockGauge label="LLM (GPT-5.2)" usedPct={f.stock_levels.llm.used_pct} cost={f.stock_levels.llm.cost} budget={f.stock_levels.llm.budget} icon={Brain} color="text-violet-500" />
          <StockGauge label="TTS (Voice)" usedPct={f.stock_levels.tts.used_pct} cost={f.stock_levels.tts.cost} budget={f.stock_levels.tts.budget} icon={MessageSquare} color="text-blue-500" />
          <StockGauge label="STT (Whisper)" usedPct={f.stock_levels.stt.used_pct} cost={f.stock_levels.stt.cost} budget={f.stock_levels.stt.budget} icon={Activity} color="text-teal-500" />
          <StockGauge label="Storage" usedPct={f.stock_levels.storage.used_pct} cost={f.stock_levels.storage.cost} budget={f.stock_levels.storage.budget} icon={HardDrive} color="text-orange-500" />
        </div>
        <p className="text-[10px] text-muted-foreground/50 mt-2 text-center">
          Monthly budget: ${f.stock_levels.monthly_budget.toFixed(2)} — Recharge when levels drop below 20%
        </p>
      </div>

      {/* Cost Breakdown + Revenue by Tier + Payment Providers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cost Breakdown */}
        <div className="bg-card border border-border/50 rounded-2xl p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <Wallet className="w-4 h-4 text-red-500" /> Cost Breakdown (Monthly)
          </h3>
          <div className="space-y-2.5">
            {[
              { label: "LLM (GPT-5.2)", value: c.llm, color: "bg-violet-500" },
              { label: "TTS (Voice)", value: c.tts, color: "bg-blue-500" },
              { label: "STT (Whisper)", value: c.stt, color: "bg-teal-500" },
              { label: "Storage", value: c.storage, color: "bg-orange-500" },
              { label: "Exports/PDF", value: c.exports, color: "bg-gray-400" },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={cn("w-2.5 h-2.5 rounded-full", item.color)} />
                  <span className="text-xs text-muted-foreground">{item.label}</span>
                </div>
                <span className="text-xs font-semibold">${item.value.toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t border-border/50 pt-2 flex items-center justify-between">
              <span className="text-xs font-bold">Total</span>
              <span className="text-sm font-bold">${c.total_month.toFixed(2)}</span>
            </div>
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground/50 space-y-0.5">
            <p>{f.month_ai_messages.toLocaleString()} AI messages this month</p>
            <p>~{f.month_tts_est.toLocaleString()} TTS requests (est.)</p>
            <p>~{f.month_stt_est.toLocaleString()} STT requests (est.)</p>
          </div>
        </div>

        {/* Revenue by Tier */}
        <div className="bg-card border border-border/50 rounded-2xl p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-emerald-500" /> Revenue by Tier
          </h3>
          <div className="space-y-2.5">
            {Object.entries(r.by_tier || {}).map(([tier, info]) => (
              <div key={tier} className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-muted-foreground truncate">{tier.replace("_", " ")}</span>
                  <span className="text-[9px] text-muted-foreground/40">{info.users}u × ${info.price}</span>
                </div>
                <span className={cn("text-xs font-semibold", info.monthly_revenue > 0 ? "text-emerald-600" : "text-muted-foreground/40")}>
                  ${info.monthly_revenue.toFixed(2)}
                </span>
              </div>
            ))}
            <div className="border-t border-border/50 pt-2 flex items-center justify-between">
              <span className="text-xs font-bold">Total MRR</span>
              <span className="text-sm font-bold text-emerald-600">${r.monthly.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Providers */}
        <div className="bg-card border border-border/50 rounded-2xl p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4 text-primary" /> Payment Providers
          </h3>
          <div className="space-y-3">
            {Object.entries(f.payments.by_provider || {}).map(([prov, info]) => (
              <div key={prov} className="flex items-center justify-between p-2.5 rounded-xl bg-muted/20">
                <div className="flex items-center gap-2">
                  <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold text-white",
                    prov === "stripe" ? "bg-violet-600" : prov === "razorpay" ? "bg-blue-600" : "bg-teal-600"
                  )}>
                    {prov === "stripe" ? "S" : prov === "razorpay" ? "R" : "P"}
                  </div>
                  <div>
                    <p className="text-xs font-medium capitalize">{prov}</p>
                    <p className="text-[9px] text-muted-foreground/50">
                      {info.success}/{info.total} successful
                    </p>
                  </div>
                </div>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold">
                  MOCKED
                </span>
              </div>
            ))}
            <div className="pt-1 space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Active Subs</span>
                <span className="font-semibold">{f.payments.active_subscriptions}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Expiring (7d)</span>
                <span className={cn("font-semibold", f.payments.expiring_soon_7d > 0 ? "text-amber-600" : "text-muted-foreground")}>
                  {f.payments.expiring_soon_7d}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 7-Day Cost Trend */}
      <div className="bg-card border border-border/50 rounded-2xl p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-primary" /> 7-Day Cost Trend
        </h3>
        <div className="space-y-1.5">
          {f.cost_trend?.map((day, i) => {
            const maxCost = Math.max(...f.cost_trend.map(d => d.cost), 0.01);
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground w-12 text-right font-mono">{day.date}</span>
                <div className="flex-1 h-[10px] rounded-md bg-muted/30 overflow-hidden">
                  <div className="bg-gradient-to-r from-red-400 to-amber-400 h-full rounded-md transition-all duration-500" style={{ width: `${(day.cost / maxCost) * 100}%` }} />
                </div>
                <span className="text-[10px] font-semibold w-14 text-right">${day.cost.toFixed(2)}</span>
                <span className="text-[9px] text-muted-foreground/40 w-12 text-right">{day.ai_messages} msgs</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Refund Reason Log */}
      <RefundReasonLog />
    </div>
  );
}

const REFUND_REASONS = [
  { key: "buyer_request", label: "Buyer Request", color: "bg-blue-500" },
  { key: "dispute", label: "Dispute", color: "bg-amber-500" },
  { key: "fraud", label: "Fraud", color: "bg-red-500" },
  { key: "wrong_recipient", label: "Wrong Recipient", color: "bg-purple-500" },
];

const STATUS_STYLES = {
  hold: { label: "On Hold", icon: ShieldAlert, bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400", border: "border-amber-300 dark:border-amber-800" },
  approved: { label: "Approved", icon: CheckCircle2, bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400", border: "border-emerald-300 dark:border-emerald-800" },
  rejected: { label: "Rejected", icon: XCircle, bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", border: "border-red-300 dark:border-red-800" },
  processed: { label: "Processed", icon: CheckCircle2, bg: "bg-gray-100 dark:bg-gray-800/30", text: "text-gray-600 dark:text-gray-400", border: "border-gray-300 dark:border-gray-700" },
};

function RefundStatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.processed;
  const SIcon = style.icon;
  return (
    <span data-testid={`refund-status-${status}`} className={cn("inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border", style.bg, style.text, style.border)}>
      <SIcon className="w-3 h-3" />
      {style.label}
    </span>
  );
}

function FlaggedForReview({ flaggedLogs, onReview, reviewing }) {
  if (!flaggedLogs || flaggedLogs.length === 0) return null;

  return (
    <div data-testid="flagged-for-review" className="bg-amber-50/60 dark:bg-amber-950/15 border border-amber-200 dark:border-amber-900/40 rounded-2xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
          <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400" strokeWidth={2} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Flagged for Review</h4>
          <p className="text-[10px] text-amber-600/70 dark:text-amber-400/60">{flaggedLogs.length} refund{flaggedLogs.length !== 1 ? "s" : ""} held — manual approval required</p>
        </div>
      </div>
      <div className="space-y-2">
        {flaggedLogs.map(log => {
          const reason = REFUND_REASONS.find(r => r.key === log.reason);
          return (
            <div key={log.id} data-testid={`flagged-item-${log.id}`} className="bg-white dark:bg-card border border-amber-200/60 dark:border-amber-900/30 rounded-xl p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn("w-2 h-2 rounded-full flex-shrink-0", reason?.color || "bg-gray-400")} />
                    <span className="text-xs font-semibold">{reason?.label || log.reason}</span>
                    {log.amount != null && <span className="text-xs text-red-600 font-medium">${log.amount.toFixed(2)}</span>}
                    <RefundStatusBadge status="hold" />
                  </div>
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1.5 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {log.flag_reason}
                  </p>
                  {log.redemption_gap_hours != null && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 ml-4">
                      Redemption gap: {log.redemption_gap_hours}h (threshold: 72h)
                    </p>
                  )}
                  {log.notes && <p className="text-[10px] text-muted-foreground mt-1 ml-4 truncate">{log.notes}</p>}
                </div>
                <div className="text-right flex-shrink-0 text-[10px] text-muted-foreground">
                  <p>{log.admin_name}</p>
                  <p className="text-[9px] text-muted-foreground/50">{new Date(log.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-amber-200/40 dark:border-amber-900/20">
                <button
                  data-testid={`approve-refund-${log.id}`}
                  onClick={() => onReview(log.id, "approve")}
                  disabled={reviewing === log.id}
                  className="flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-all disabled:opacity-50"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  {reviewing === log.id ? "..." : "Approve Refund"}
                </button>
                <button
                  data-testid={`reject-refund-${log.id}`}
                  onClick={() => onReview(log.id, "reject")}
                  disabled={reviewing === log.id}
                  className="flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-all disabled:opacity-50"
                >
                  <XCircle className="w-3 h-3" />
                  {reviewing === log.id ? "..." : "Reject"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RefundReasonLog() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total_count: 0, total_amount: 0, by_reason: {}, flagged_count: 0, held_count: 0 });
  const [filterReason, setFilterReason] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ reason: "buyer_request", amount: "", payment_reference: "", gift_id: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filterReason) params.append("reason", filterReason);
      if (filterStatus) params.append("status", filterStatus);
      if (searchQuery) params.append("search", searchQuery);
      const { data } = await axios.get(`${API}/admin/refunds?${params.toString()}`, { headers });
      setLogs(data.logs || []);
      setStats({
        total_count: data.total_count,
        total_amount: data.total_amount,
        by_reason: data.by_reason || {},
        flagged_count: data.flagged_count || 0,
        held_count: data.held_count || 0,
      });
    } catch (err) {
      /* silently fail */
    }
  };

  useEffect(() => { fetchLogs(); }, [filterReason, filterStatus, searchQuery]);

  const submitRefund = async () => {
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/admin/refunds/log`, {
        reason: formData.reason,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        payment_reference: formData.payment_reference || null,
        gift_id: formData.gift_id || null,
        notes: formData.notes,
      }, { headers });
      if (resp.data.flagged) {
        toast.warning("Refund auto-flagged for review", {
          description: resp.data.flag_reason,
        });
      } else {
        toast.success("Refund logged");
      }
      setShowAddForm(false);
      setFormData({ reason: "buyer_request", amount: "", payment_reference: "", gift_id: "", notes: "" });
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log refund");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReview = async (refundId, action) => {
    setReviewing(refundId);
    try {
      await axios.post(`${API}/admin/refunds/review`, {
        refund_id: refundId,
        action,
        admin_notes: "",
      }, { headers });
      toast.success(`Refund ${action === "approve" ? "approved" : "rejected"}`);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} refund`);
    } finally {
      setReviewing(null);
    }
  };

  const flaggedLogs = logs.filter(l => l.status === "hold");
  const allLogs = logs;

  return (
    <div data-testid="refund-reason-log" className="bg-card border border-border/50 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <RotateCcw className="w-4 h-4 text-red-500" /> Refund Reason Log
        </h3>
        <button
          data-testid="add-refund-btn"
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-all"
        >
          <Plus className="w-3 h-3" /> Log Refund
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <div className="bg-muted/30 rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold">{stats.total_count}</p>
          <p className="text-[10px] text-muted-foreground">Total Refunds</p>
        </div>
        <div className="bg-muted/30 rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-red-600">${stats.total_amount.toFixed(2)}</p>
          <p className="text-[10px] text-muted-foreground">Total Amount</p>
        </div>
        <div className="bg-muted/30 rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-amber-600">{stats.flagged_count}</p>
          <p className="text-[10px] text-muted-foreground">Total Flagged</p>
        </div>
        <div className={cn("rounded-xl p-2.5 text-center", stats.held_count > 0 ? "bg-amber-50 dark:bg-amber-950/20 ring-1 ring-amber-300 dark:ring-amber-800" : "bg-muted/30")}>
          <p className={cn("text-lg font-bold", stats.held_count > 0 ? "text-amber-600 animate-pulse" : "")}>{stats.held_count}</p>
          <p className="text-[10px] text-muted-foreground">Pending Review</p>
        </div>
        {REFUND_REASONS.slice(0, 1).map(r => (
          <div key={r.key} className="bg-muted/30 rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold">{stats.by_reason[r.key] || 0}</p>
            <p className="text-[10px] text-muted-foreground">{r.label}</p>
          </div>
        ))}
      </div>

      {/* Add Refund Form */}
      {showAddForm && (
        <div data-testid="refund-form" className="bg-muted/20 rounded-2xl p-4 mb-4 space-y-3 animate-in fade-in">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium mb-1 block">Reason</label>
              <select
                data-testid="refund-reason-select"
                value={formData.reason}
                onChange={e => setFormData(f => ({ ...f, reason: e.target.value }))}
                className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
              >
                {REFUND_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium mb-1 block">Amount ($)</label>
              <input
                data-testid="refund-amount-input"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={e => setFormData(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium mb-1 block">Payment Ref</label>
              <input
                value={formData.payment_reference}
                onChange={e => setFormData(f => ({ ...f, payment_reference: e.target.value }))}
                placeholder="Optional"
                className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium mb-1 block">Gift ID</label>
              <input
                data-testid="refund-gift-id-input"
                value={formData.gift_id}
                onChange={e => setFormData(f => ({ ...f, gift_id: e.target.value }))}
                placeholder="Optional — triggers auto-flag check"
                className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium mb-1 block">Notes</label>
            <textarea
              data-testid="refund-notes-input"
              value={formData.notes}
              onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
              maxLength={1000}
              rows={2}
              placeholder="Add details..."
              className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card resize-none"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAddForm(false)} className="text-xs px-3 py-1.5 rounded-full border border-border/50 text-muted-foreground hover:bg-muted/30">Cancel</button>
            <button
              data-testid="submit-refund-btn"
              onClick={submitRefund}
              disabled={submitting}
              className="text-xs px-4 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? "Logging..." : "Log Refund"}
            </button>
          </div>
        </div>
      )}

      {/* Flagged for Review Section */}
      <FlaggedForReview flaggedLogs={flaggedLogs} onReview={handleReview} reviewing={reviewing} />

      {/* Search & Filter */}
      <div className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            data-testid="refund-search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search refunds..."
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-border/50 bg-card focus:outline-none focus:border-primary"
          />
        </div>
        <select
          data-testid="refund-filter"
          value={filterReason}
          onChange={e => setFilterReason(e.target.value)}
          className="text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
        >
          <option value="">All Reasons</option>
          {REFUND_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select
          data-testid="refund-status-filter"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
        >
          <option value="">All Status</option>
          <option value="hold">On Hold</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="processed">Processed</option>
        </select>
      </div>

      {/* Log Table */}
      <div className="space-y-1.5 max-h-64 overflow-y-auto">
        {allLogs.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">No refunds logged yet</p>
        )}
        {allLogs.map(log => {
          const reason = REFUND_REASONS.find(r => r.key === log.reason);
          return (
            <div key={log.id} data-testid={`refund-row-${log.id}`} className={cn(
              "flex items-center gap-3 p-2.5 rounded-xl transition-all",
              log.status === "hold" ? "bg-amber-50/50 dark:bg-amber-950/10 hover:bg-amber-50 dark:hover:bg-amber-950/20" : "bg-muted/20 hover:bg-muted/30"
            )}>
              <span className={cn("w-2 h-2 rounded-full flex-shrink-0", reason?.color || "bg-gray-400")} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-semibold">{reason?.label || log.reason}</span>
                  {log.amount != null && <span className="text-[10px] text-red-600 font-medium">${log.amount.toFixed(2)}</span>}
                  <RefundStatusBadge status={log.status || "processed"} />
                  {log.flagged && <span className="text-[9px] text-amber-600 font-medium">AUTO-FLAGGED</span>}
                </div>
                {log.flag_reason && <p className="text-[10px] text-amber-600/80 dark:text-amber-400/60 truncate">{log.flag_reason}</p>}
                {log.notes && <p className="text-[10px] text-muted-foreground truncate">{log.notes}</p>}
                {log.reviewed_by_name && (
                  <p className="text-[9px] text-muted-foreground/50">
                    Reviewed by {log.reviewed_by_name} on {new Date(log.reviewed_at).toLocaleDateString()}
                    {log.review_notes ? ` — ${log.review_notes}` : ""}
                  </p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] text-muted-foreground">{log.admin_name}</p>
                <p className="text-[9px] text-muted-foreground/50">{new Date(log.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function MarketingKitPanel() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const assets = [
    { name: "Global Launch Hero", file: "global-launch-hero.jpg", type: "image", desc: "Website hero, press kit, email header" },
    { name: "Instagram / Facebook Ad", file: "social-instagram.jpg", type: "image", desc: "Square format — Instagram feed, Facebook ads" },
    { name: "Africa Cultural Campaign", file: "africa-cultural.jpg", type: "image", desc: "West African market — social ads, billboards" },
    { name: "Group Plan Ad", file: "group-plan-ad.jpg", type: "image", desc: "Group subscription promo, LinkedIn, email" },
    { name: "LinkedIn Professional", file: "linkedin-professional.jpg", type: "image", desc: "Enterprise outreach, LinkedIn ads" },
    { name: "App Store Promo", file: "appstore-promo.jpg", type: "image", desc: "App Store feature graphic, portrait format" },
  ];

  const documents = [
    { name: "Global Launch Plan", desc: "Complete 12-part marketing strategy — channels, costs, media engagement, launch checklist", icon: FileText },
    { name: "Ad Copy Library", desc: "Ready-to-use ad copy for Instagram, Facebook, Google, TikTok, Twitter, LinkedIn, email, WhatsApp", icon: FileText },
  ];

  return (
    <div className="space-y-6" data-testid="marketing-kit-panel">
      {/* Header + Download All */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" /> Marketing Kit
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Launch materials, ad creatives, and strategy documents</p>
        </div>
        <a href={`${backendUrl}/api/download/marketing-kit`} download
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium bg-primary text-primary-foreground hover:opacity-90 transition-all"
          data-testid="download-marketing-kit-btn">
          <Package className="w-4 h-4" /> Download Complete Kit (3.3 MB)
        </a>
      </div>

      {/* Ad Creatives Grid */}
      <div>
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Image className="w-4 h-4 text-muted-foreground" /> Ad Creatives</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {assets.map((a) => (
            <div key={a.file} className="group bg-card border border-border/50 rounded-xl overflow-hidden hover:border-primary/30 transition-all">
              <div className="aspect-video relative overflow-hidden bg-muted/30">
                <img src={`/marketing-kit/${a.file}`} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="text-sm font-medium">{a.name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{a.desc}</p>
                <a href={`/marketing-kit/${a.file}`} download className="flex items-center gap-1 text-[10px] text-primary hover:underline mt-2">
                  <Download className="w-3 h-3" /> Download
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strategy Documents */}
      <div>
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" /> Strategy Documents</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {documents.map((d) => (
            <div key={d.name} className="bg-card border border-border/50 rounded-xl p-4 hover:border-primary/30 transition-all">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <d.icon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{d.name}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">{d.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Downloads */}
      <div className="bg-muted/20 rounded-xl p-4 border border-border/30">
        <h3 className="text-sm font-medium mb-3">Quick Downloads</h3>
        <div className="flex flex-wrap gap-2">
          <a href={`${backendUrl}/api/download/marketing-kit`} download className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-card border border-border/50 hover:border-primary/30 transition-all" data-testid="quick-download-marketing">
            <Package className="w-3.5 h-3.5 text-primary" /> Marketing Kit (.zip)
          </a>
          <a href={`${backendUrl}/api/download/html-website`} download className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-card border border-border/50 hover:border-primary/30 transition-all" data-testid="quick-download-html">
            <Globe className="w-3.5 h-3.5 text-emerald-500" /> HTML Website (.zip)
          </a>
          <a href={`${backendUrl}/api/download/elementor-templates`} download className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-card border border-border/50 hover:border-primary/30 transition-all" data-testid="quick-download-elementor">
            <FileText className="w-3.5 h-3.5 text-amber-500" /> Elementor Templates (.zip)
          </a>
          <a href={`${backendUrl}/api/download/marketing-site`} download className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-card border border-border/50 hover:border-primary/30 transition-all" data-testid="quick-download-react">
            <Layers className="w-3.5 h-3.5 text-violet-500" /> React Build (.zip)
          </a>
        </div>
      </div>
    </div>
  );
}


function ReviewsPanel({ token }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/testimonials/pending`, { headers });
      setPending(res.data.pending || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchPending(); }, []);

  const approve = async (id) => {
    try {
      await axios.put(`${API}/admin/testimonials/${id}/approve`, {}, { headers });
      toast.success("Review approved and published");
      fetchPending();
    } catch { toast.error("Failed to approve"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers });
      toast.success("Review removed");
      fetchPending();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="space-y-4" data-testid="reviews-panel">
      <h3 className="text-sm font-semibold flex items-center gap-1.5">
        <PenLine className="w-4 h-4 text-primary" /> User Reviews
        {pending.length > 0 && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">{pending.length} pending</span>}
      </h3>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : pending.length === 0 ? (
        <div className="text-center py-8 bg-card border border-border/50 rounded-2xl">
          <CheckCircle2 className="w-8 h-8 text-emerald-500/50 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No pending reviews</p>
          <p className="text-[11px] text-muted-foreground/70 mt-0.5">User-submitted reviews will appear here for approval</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pending.map(r => (
            <div key={r.id} data-testid={`pending-review-${r.id}`} className="bg-card border border-border/50 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium bg-primary/10 text-primary">
                    {r.avatar_initials || "?"}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{r.name}</p>
                    {r.role && <p className="text-[11px] text-muted-foreground">{r.role}</p>}
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {Array.from({ length: r.rating || 5 }).map((_, i) => (
                    <Star key={i} className="w-3 h-3 text-amber-500 fill-amber-500" />
                  ))}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3 italic">"{r.quote}"</p>
              <div className="flex gap-2">
                <button onClick={() => approve(r.id)} className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 transition-colors">
                  Approve & Publish
                </button>
                <button onClick={() => remove(r.id)} className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 transition-colors">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IncidentsPanel({ token }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", detail: "", status: "investigating", services: "" });
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/status/incidents`);
      setIncidents(res.data.incidents || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchIncidents(); }, []);

  const createIncident = async () => {
    try {
      await axios.post(`${API}/admin/incidents`, {
        title: form.title,
        detail: form.detail,
        status: form.status,
        services: form.services ? form.services.split(",").map(s => s.trim()) : [],
      }, { headers });
      toast.success("Incident created");
      setShowForm(false);
      setForm({ title: "", detail: "", status: "investigating", services: "" });
      fetchIncidents();
    } catch { toast.error("Failed to create incident"); }
  };

  const resolveIncident = async (inc) => {
    try {
      await axios.put(`${API}/admin/incidents/resolve`, {
        date: inc.date,
        title: inc.title,
        detail: `Resolved: ${inc.detail}`,
      }, { headers });
      toast.success("Incident resolved");
      fetchIncidents();
    } catch { toast.error("Failed to resolve"); }
  };

  return (
    <div className="space-y-4" data-testid="incidents-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <ShieldAlert className="w-4 h-4 text-primary" /> Status Incidents
        </h3>
        <button data-testid="create-incident-btn" onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Incident
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border/50 rounded-2xl p-4 space-y-3" data-testid="incident-form">
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Incident title" className="w-full bg-muted/50 border border-border/50 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          <textarea value={form.detail} onChange={e => setForm({ ...form, detail: e.target.value })} placeholder="Description of the issue..." className="w-full bg-muted/50 border border-border/50 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary h-20 resize-none" />
          <div className="flex gap-3">
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="bg-muted/50 border border-border/50 rounded-xl px-3 py-2 text-sm focus:outline-none">
              <option value="investigating">Investigating</option>
              <option value="identified">Identified</option>
              <option value="monitoring">Monitoring</option>
              <option value="resolved">Resolved</option>
            </select>
            <input value={form.services} onChange={e => setForm({ ...form, services: e.target.value })} placeholder="Affected services (comma-separated)" className="flex-1 bg-muted/50 border border-border/50 rounded-xl px-3 py-2 text-sm focus:outline-none" />
          </div>
          <div className="flex gap-2">
            <button data-testid="submit-incident-btn" onClick={createIncident} disabled={!form.title} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50">Publish</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl bg-muted/50 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-2">
          {incidents.map((inc, i) => {
            const isOp = inc.status === "operational" || inc.resolved;
            return (
              <div key={i} data-testid={`admin-incident-${i}`} className="bg-card border border-border/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className={cn("w-2.5 h-2.5 rounded-full", isOp ? "bg-emerald-500" : "bg-amber-500")} />
                    <p className="text-sm font-medium">{inc.title}</p>
                    {!isOp && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">{inc.status}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">{inc.date}</span>
                    {!isOp && (
                      <button onClick={() => resolveIncident(inc)} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 transition-colors">
                        Resolve
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground ml-4">{inc.detail}</p>
                {inc.services?.length > 0 && (
                  <div className="flex gap-1.5 mt-2 ml-4">
                    {inc.services.map(s => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground">{s}</span>)}
                  </div>
                )}
              </div>
            );
          })}
          {!incidents.length && <p className="text-xs text-muted-foreground text-center py-6">No incidents recorded</p>}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [finData, setFinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [activeTab, setActiveTab] = useState("operations");
  const intervalRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const [opsResp, finResp] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/financials`, { headers }),
      ]);
      setData(opsResp.data);
      setFinData(finResp.data);
    } catch (err) {
      if (err.response?.status === 403) setError("admin");
      else setError("Failed to load dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchDashboard(); }, []);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchDashboard, 30000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh]);

  if (error === "admin") {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Shield className="w-12 h-12 text-destructive/30 mx-auto mb-3" />
            <h2 className="text-lg font-heading font-semibold mb-1">Admin Access Required</h2>
            <p className="text-sm text-muted-foreground mb-4">You don&apos;t have permission to view this page.</p>
            <button onClick={() => navigate("/chat")} className="text-sm text-primary hover:underline">Go to Chat</button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-5 sm:py-7">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" /> Operations Dashboard
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">Live platform metrics — Admin only</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="auto-refresh-toggle"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium border transition-all",
                  autoRefresh
                    ? "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300/50 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-300"
                    : "border-border/50 text-muted-foreground hover:bg-muted/50"
                )}
              >
                <Timer className={cn("w-3.5 h-3.5", autoRefresh && "animate-pulse")} />
                {autoRefresh ? "Live" : "Auto"}
              </button>
              <button
                data-testid="refresh-dashboard"
                onClick={fetchDashboard}
                disabled={loading}
                className="p-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
              >
                <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-1 mb-5 bg-muted/30 rounded-2xl p-1 w-fit">
            {[
              { key: "operations", label: "Operations", icon: Activity },
              { key: "financials", label: "Financials", icon: DollarSign },
              { key: "providers", label: "Status", icon: Wifi },
              { key: "incidents", label: "Incidents", icon: ShieldAlert },
              { key: "reviews", label: "Reviews", icon: PenLine },
              { key: "marketing", label: "Marketing", icon: Globe },
            ].map(tab => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  "flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all",
                  activeTab === tab.key
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {loading && !data ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : data ? (
            <div className="space-y-5">

              {activeTab === "operations" && data && (
                <div className="space-y-5">

              {/* Operational Alerts */}
              {data.alerts?.length > 0 && (
                <div data-testid="alerts-panel" className="space-y-2">
                  {data.alerts.map((alert, i) => {
                    const style = ALERT_STYLES[alert.level] || ALERT_STYLES.info;
                    const AIcon = style.icon;
                    return (
                      <div key={i} data-testid={`alert-${i}`} className={cn("flex items-center gap-2.5 px-4 py-2.5 rounded-xl border", style.bg, style.border)}>
                        <AIcon className={cn("w-4 h-4 flex-shrink-0", style.text)} strokeWidth={2} />
                        <span className={cn("text-xs font-medium", style.text)}>{alert.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* KPI Row — Top Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                <MetricCard icon={Users} label="Total Users" value={data.total_users} sub={`+${data.recent_signups_7d} this week`} testId="metric-total-users" />
                <MetricCard icon={Zap} label="Active Today" value={data.active_users_today} sub={`${data.active_users_7d} in 7d`} color="text-emerald-600" bgColor="bg-emerald-100 dark:bg-emerald-900/30" testId="metric-active-today" />
                <MetricCard icon={MessageSquare} label="Conversations" value={data.total_conversations} sub={`${data.avg_convs_per_user} avg/user`} color="text-blue-600" bgColor="bg-blue-100 dark:bg-blue-900/30" testId="metric-conversations" />
                <MetricCard icon={Layers} label="Messages" value={data.total_messages} sub={`${data.avg_msgs_per_user} avg/user`} color="text-violet-600" bgColor="bg-violet-100 dark:bg-violet-900/30" testId="metric-messages" />
                <MetricCard icon={TrendingUp} label="Conversion Rate" value={`${data.conversion_rate}%`} sub={`${data.paid_users} paid users`} color="text-amber-600" bgColor="bg-amber-100 dark:bg-amber-900/30" testId="metric-conversion" />
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
                <MetricCard icon={Heart} label="Mood Logs" value={data.total_mood_logs} sub={data.platform_avg_mood ? `Avg: ${data.platform_avg_mood}/10` : undefined} color="text-pink-600" bgColor="bg-pink-100 dark:bg-pink-900/30" />
                <MetricCard icon={Brain} label="Memories" value={data.total_memories} color="text-amber-600" bgColor="bg-amber-100 dark:bg-amber-900/30" />
                <MetricCard icon={Target} label="Goals" value={data.total_goals} sub={`${data.active_goals} active, ${data.completed_goals} done`} color="text-orange-600" bgColor="bg-orange-100 dark:bg-orange-900/30" />
                <MetricCard icon={Bookmark} label="Bookmarks" value={data.total_bookmarks} color="text-teal-600" bgColor="bg-teal-100 dark:bg-teal-900/30" />
                <MetricCard icon={Share2} label="Shared" value={data.total_shared_insights} color="text-indigo-600" bgColor="bg-indigo-100 dark:bg-indigo-900/30" />
                <MetricCard icon={CalendarDays} label="Events" value={data.total_calendar_events} color="text-cyan-600" bgColor="bg-cyan-100 dark:bg-cyan-900/30" />
              </div>

              {/* Main Grid: 3 columns */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* 7-Day Growth Trend */}
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Activity className="w-4 h-4 text-primary" /> 7-Day Activity
                  </h3>
                  <div className="space-y-1.5">
                    {data.growth_trend?.map((day, i) => {
                      const maxVal = Math.max(...data.growth_trend.map(d => d.conversations + d.moods + d.signups), 1);
                      const total = day.conversations + day.moods + day.signups;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-[10px] text-muted-foreground w-12 text-right font-mono">{day.date}</span>
                          <div className="flex-1 h-[14px] rounded-md bg-muted/30 overflow-hidden flex">
                            {day.conversations > 0 && (
                              <div className="bg-blue-400 h-full" style={{ width: `${(day.conversations / maxVal) * 100}%` }} title={`${day.conversations} convs`} />
                            )}
                            {day.moods > 0 && (
                              <div className="bg-pink-400 h-full" style={{ width: `${(day.moods / maxVal) * 100}%` }} title={`${day.moods} moods`} />
                            )}
                            {day.signups > 0 && (
                              <div className="bg-emerald-400 h-full" style={{ width: `${(day.signups / maxVal) * 100}%` }} title={`${day.signups} signups`} />
                            )}
                          </div>
                          <span className="text-[10px] font-semibold w-6 text-right">{total}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 mt-2.5">
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground/50"><span className="w-2 h-2 rounded-sm bg-blue-400" />Chats</span>
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground/50"><span className="w-2 h-2 rounded-sm bg-pink-400" />Moods</span>
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground/50"><span className="w-2 h-2 rounded-sm bg-emerald-400" />Signups</span>
                  </div>
                </div>

                {/* Tier Breakdown */}
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-primary" /> Subscription Tiers
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(data.tier_breakdown || {}).map(([tier, count]) => (
                      <BarRow key={tier} label={tier.replace("_", " ")} value={count} max={data.total_users} color={
                        tier === "diamond" ? "bg-violet-500" : tier === "platinum" ? "bg-amber-500" : tier === "team_dev" ? "bg-emerald-500" : tier === "omni_plus" ? "bg-blue-500" : tier === "family" ? "bg-pink-500" : "bg-muted-foreground/30"
                      } />
                    ))}
                  </div>
                </div>

                {/* Module Usage */}
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4 text-primary" /> Module Usage
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(data.module_usage || {}).slice(0, 8).map(([mod, count]) => {
                      const maxVal = Math.max(...Object.values(data.module_usage || {}));
                      return <BarRow key={mod} label={mod.replace("_", " ")} value={count} max={maxVal} color="bg-primary" />;
                    })}
                    {!Object.keys(data.module_usage || {}).length && (
                      <p className="text-xs text-muted-foreground text-center py-3">No data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Grid: Mood + Language + Activity Feed + Top Users */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                {/* Mood Distribution */}
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Heart className="w-4 h-4 text-pink-500" /> Mood Health
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(data.mood_breakdown || {}).map(([mood, count]) => {
                      const maxVal = Math.max(...Object.values(data.mood_breakdown || {}));
                      return <BarRow key={mood} label={mood} value={count} max={maxVal} color={
                        mood === "positive" ? "bg-emerald-500" : mood === "negative" ? "bg-red-500" : mood === "crisis" ? "bg-red-700" : "bg-amber-500"
                      } />;
                    })}
                    {data.platform_avg_mood && (
                      <p className="text-[10px] text-muted-foreground/50 text-center mt-2">Platform avg: {data.platform_avg_mood}/10</p>
                    )}
                    {data.crisis_moods_7d > 0 && (
                      <div className="flex items-center gap-1 mt-1 px-2 py-1 rounded-lg bg-red-50 dark:bg-red-950/20">
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                        <span className="text-[10px] font-medium text-red-600 dark:text-red-400">{data.crisis_moods_7d} crisis moods (7d)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Language Distribution */}
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Globe className="w-4 h-4 text-primary" /> Languages
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(data.language_distribution || {}).slice(0, 8).map(([langCode, count]) => {
                      const maxVal = Math.max(...Object.values(data.language_distribution || {}));
                      return <BarRow key={langCode} label={langCode || "en"} value={count} max={maxVal} color="bg-cyan-500" />;
                    })}
                    {!Object.keys(data.language_distribution || {}).length && (
                      <p className="text-xs text-muted-foreground text-center py-3">No data</p>
                    )}
                  </div>
                </div>

                {/* Top Users Today */}
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Flame className="w-4 h-4 text-orange-500" /> Top Users Today
                  </h3>
                  {data.top_users_today?.length > 0 ? (
                    <div className="space-y-2">
                      {data.top_users_today.map((u, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className={cn(
                            "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                            i === 0 ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600" : "bg-muted/50 text-muted-foreground"
                          )}>{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{u.username}</p>
                            <p className="text-[9px] text-muted-foreground/50">{u.tier}</p>
                          </div>
                          <span className="text-xs font-bold">{u.sessions_today}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-3">No activity today</p>
                  )}
                </div>

                {/* Activity Feed */}
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Activity className="w-4 h-4 text-primary" /> Recent Activity
                  </h3>
                  {data.activity_feed?.length > 0 ? (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {data.activity_feed.slice(0, 10).map((evt, i) => {
                        const FIcon = FEED_ICONS[evt.type] || Activity;
                        return (
                          <div key={i} className="flex items-start gap-2">
                            <FIcon className="w-3.5 h-3.5 text-muted-foreground/50 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                            <div className="min-w-0">
                              <p className="text-[11px] leading-tight">{evt.text}</p>
                              {evt.time && (
                                <p className="text-[9px] text-muted-foreground/40">{new Date(evt.time).toLocaleDateString()}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-3">No recent activity</p>
                  )}
                </div>
              </div>

              </div>
              )}

              {/* ═══════════════════════ FINANCIALS TAB ═══════════════════════ */}
              {activeTab === "financials" && finData && (
                <FinancialCenter data={finData} />
              )}

              {/* ═══════════════════════ PROVIDERS TAB ═══════════════════════ */}
              {activeTab === "providers" && (
                <ProvidersPanel />
              )}

              {/* ═══════════════════════ INCIDENTS TAB ═══════════════════════ */}
              {activeTab === "incidents" && (
                <IncidentsPanel token={token} />
              )}

              {/* ═══════════════════════ REVIEWS TAB ═══════════════════════ */}
              {activeTab === "reviews" && (
                <ReviewsPanel token={token} />
              )}

              {activeTab === "marketing" && (
                <MarketingKitPanel />
              )}

              {/* Timestamp */}
              <p className="text-[10px] text-muted-foreground/40 text-center flex items-center justify-center gap-1">
                <Clock className="w-3 h-3" />
                {autoRefresh ? "Auto-refreshing every 30s • " : ""}
                Last updated: {new Date(data.timestamp).toLocaleString()}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-sm text-destructive">{error}</p>
              <button onClick={fetchDashboard} className="text-sm text-primary hover:underline mt-2">Retry</button>
            </div>
          ) : null}
        </div>
      </div>
    </AppLayout>
  );
}
